import { useState } from "react";
import { useSaveValveDetails, type InspectionDetail, type Lookup } from "../../api/inspections";

type Form = Record<
  | "manufacturer"
  | "model_no"
  | "serial_no"
  | "pressure_rating"
  | "body_material"
  | "flow_direction"
  | "pilot_manufacturer"
  | "pilot_model_no"
  | "pilot_size"
  | "pilot_part_number",
  string
>;

function LookupSelect({
  label,
  lookup,
  value,
  onChange,
  disabled,
}: {
  label: string;
  lookup: Lookup;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
}) {
  return (
    <label>
      {label}
      <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
        <option value="">—</option>
        {lookup.values.map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
      </select>
      {lookup.note ? <span className="field-hint">{lookup.note}</span> : null}
    </label>
  );
}

/** Sections 3 and 4: the valve and its pilot. Every drop-down is the
 * checklist's own list; the server refuses a value not on it. The
 * manufacturer defaults to what the QCP named, and yields to the field
 * operators. A choice saves as it is made; a typed value when the box is
 * left. */
export default function IdentityTab({ detail }: { detail: InspectionDetail }) {
  const save = useSaveValveDetails(detail.inspection.id);
  const canEdit = detail.actions.can_edit;
  const v = detail.valve_details;
  const p = detail.pilot;
  const lookups = detail.options.lookups;
  const [form, setForm] = useState<Form>({
    manufacturer: v.manufacturer ?? detail.qcp.manufacturer ?? "",
    model_no: v.model_no ?? "",
    serial_no: v.serial_no ?? "",
    pressure_rating: v.pressure_rating ?? "",
    body_material: v.body_material ?? "",
    flow_direction: v.flow_direction ?? "",
    pilot_manufacturer: p.manufacturer ?? "",
    pilot_model_no: p.model_no ?? "",
    pilot_size: p.size ?? "",
    pilot_part_number: p.part_number ?? "",
  });
  // What the server last agreed to, so leaving a box unchanged sends nothing.
  // The QCP's manufacturer counts as unsaved until a change carries it up.
  const [saved, setSaved] = useState<Form>({ ...form, manufacturer: v.manufacturer ?? "" });

  function commit(next: Form) {
    setForm(next);
    if (JSON.stringify(next) === JSON.stringify(saved)) {
      return;
    }
    const body = Object.fromEntries(Object.entries(next).map(([k, val]) => [k, val === "" ? null : val]));
    save.mutate({ valve_id: detail.active_valve_id, ...body }, { onSuccess: () => setSaved(next) });
  }
  const set = (key: keyof Form, value: string) => setForm((f) => ({ ...f, [key]: value }));
  const choose = (key: keyof Form, value: string) => commit({ ...form, [key]: value });
  const blur = () => commit(form);

  return (
    <div className="insp-section">
      <div className="insp-section-head">
        <h3>3–4 · Valve and pilot</h3>
        <span className="insp-section-sub">{canEdit ? "Saves as you go" : ""}</span>
      </div>
      <div className="insp-fields">
        <h4 className="insp-fields-span">Section 3 – Valve identification</h4>
        <LookupSelect label="3.1 Manufacturer" lookup={lookups.manufacturers} value={form.manufacturer} onChange={(x) => choose("manufacturer", x)} disabled={!canEdit} />
        <label>
          3.2 Model no.
          <input value={form.model_no} onChange={(e) => set("model_no", e.target.value)} onBlur={blur} readOnly={!canEdit} />
        </label>
        <label>
          3.3 Serial no.
          <input value={form.serial_no} onChange={(e) => set("serial_no", e.target.value)} onBlur={blur} readOnly={!canEdit} />
        </label>
        <LookupSelect label="3.4 Pressure rating" lookup={lookups.pressure_ratings} value={form.pressure_rating} onChange={(x) => choose("pressure_rating", x)} disabled={!canEdit} />
        <LookupSelect label="3.5 Body material" lookup={lookups.body_materials} value={form.body_material} onChange={(x) => choose("body_material", x)} disabled={!canEdit} />
        <LookupSelect label="3.6 Flow direction" lookup={lookups.flow_directions} value={form.flow_direction} onChange={(x) => choose("flow_direction", x)} disabled={!canEdit} />
        <h4 className="insp-fields-span">Section 4 – Pilot control system</h4>
        <LookupSelect label="4.1 Pilot manufacturer" lookup={lookups.manufacturers} value={form.pilot_manufacturer} onChange={(x) => choose("pilot_manufacturer", x)} disabled={!canEdit} />
        <label>
          4.2 Pilot model no.
          <input value={form.pilot_model_no} onChange={(e) => set("pilot_model_no", e.target.value)} onBlur={blur} readOnly={!canEdit} />
        </label>
        <LookupSelect label="4.3 Pilot size" lookup={lookups.pilot_valve_sizes} value={form.pilot_size} onChange={(x) => choose("pilot_size", x)} disabled={!canEdit} />
        <label>
          4.4 Pilot part number
          <input value={form.pilot_part_number} onChange={(e) => set("pilot_part_number", e.target.value)} onBlur={blur} readOnly={!canEdit} />
        </label>
      </div>
      {save.isError ? <p className="notice bad">Not saved: {save.error.message}</p> : null}
    </div>
  );
}
